/**
 * FE-side mirror of the backend's role-to-class mapping for the in-call
 * surface. The source of truth for role identifiers is
 * `src/lib/rbac/roles.ts` (ROLE_TYPES); add new roles here when the BE adds
 * them to that enum.
 *
 * Mapping rationale:
 * - PRACTITIONER: clinical staff who lead sessions. Mirrors
 *   src/lib/rbac/roles.ts CLINICAL_ROLES.
 * - OBSERVER: non-clinical staff with BA-wide visibility. Sees the call,
 *   participants list (read-only), and call status, but no moderation
 *   surface (mute/kick) and no Ask Elixaa access.
 * - CLIENT: the person being seen. Minimal FE privilege.
 */

export type EffectiveClass = 'practitioner' | 'client' | 'observer';

const PRACTITIONER_ROLES = new Set(['psychologist', 'chief_psychologist', 'volunteer']);

const OBSERVER_ROLES = new Set([
  'ba_admin',
  'ba_super_admin',
  'super_admin',
  'org_user',
  'customer_support',
]);

/**
 * Maps a backend role string to one of three FE permission classes.
 * Unknown roles default to 'client' (least FE privilege) with a dev
 * warning — falling through to 'observer' on unknown input would grant
 * moderation/observation access in the UI, which is a minor
 * privilege-escalation hazard. The backend still enforces real
 * authorization; this helper just controls FE surface visibility.
 */
export function toEffectiveClass(role: string): EffectiveClass {
  if (PRACTITIONER_ROLES.has(role)) return 'practitioner';
  if (role === 'client') return 'client';
  if (OBSERVER_ROLES.has(role)) return 'observer';
  if (import.meta.env.DEV) {
    console.warn(
      `[toEffectiveClass] unknown role "${role}" — defaulting to 'client' (least FE privilege)`,
    );
  }
  return 'client';
}
